import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { Color, Key } from 'chessground/types';
import { Chess } from 'chess.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faRotateLeft,
  faRotateRight,
  faRobot,
  faLightbulb,
} from '@fortawesome/free-solid-svg-icons';
import { toDests } from '../utils/ChessUtils';
import { stockfishEngine } from '../utils/StockfishEngine';
import { clientLog } from '../utils/clientLog';
import { whiteWinPct } from './winChance';
import { LinePosition } from './CorrectionBoard';
import { PlayLevel, PLAY_LEVELS, loadPlayLevel, savePlayLevel } from './playOutLevels';

// Playing a position out against the engine, always from one of your own games, never from move 1: it
// answers "could I actually have converted this?". Runs in the browser with the same Stockfish worker,
// weakened to the chosen level (playOutLevels.ts). Nothing is saved.

// Minimum delay before the engine's reply, so an instant reply doesn't read as a rejected move.
const MIN_REPLY_MS = 350;

// How deep the hint looks: it only has to name the piece.
const HINT_DEPTH = 12;

// Chessground needs its four default brushes before a custom one. The ring matches the repair hint
// (see CorrectionBoard).
const HINT_BRUSHES = {
  green: { key: 'g', color: '#15781B', opacity: 1, lineWidth: 10 },
  red: { key: 'r', color: '#882020', opacity: 1, lineWidth: 10 },
  blue: { key: 'b', color: '#003088', opacity: 1, lineWidth: 10 },
  yellow: { key: 'y', color: '#e68f00', opacity: 1, lineWidth: 10 },
  hintPiece: { key: 'hintPiece', color: '#7cd3ff', opacity: 0.95, lineWidth: 12 },
};

type Result = {
  // Who it went to, from the player's point of view.
  outcome: 'won' | 'lost' | 'drawn';
  reason: string;
};

function resultOf(fen: string, playerColor: Color): Result | null {
  let c: Chess;
  try {
    c = new Chess(fen);
  } catch {
    return null;
  }
  if (!c.isGameOver()) return null;
  const playerToMove = (c.turn() === 'w' ? 'white' : 'black') === playerColor;
  if (c.isCheckmate()) {
    // The side to move is the one that's mated.
    return playerToMove
      ? { outcome: 'lost', reason: 'Checkmate.' }
      : { outcome: 'won', reason: 'Checkmate.' };
  }
  if (c.isStalemate()) return { outcome: 'drawn', reason: 'Stalemate.' };
  if (c.isInsufficientMaterial())
    return { outcome: 'drawn', reason: 'Not enough material to mate.' };
  if (c.isThreefoldRepetition()) return { outcome: 'drawn', reason: 'Threefold repetition.' };
  return { outcome: 'drawn', reason: 'Draw.' };
}

export default function PlayOutBoard(props: {
  // The position to play out, and the player's side in the game it came from.
  startFen: string;
  playerColor: Color;
  // Stored evaluation of the start (white centipawns), so the "you started here" marker shows at once.
  startEvalCp?: number | null;
  // Called whenever a game finishes, for analytics on the caller's side.
  onFinished?: (outcome: Result['outcome'], levelKey: string) => void;
}): React.ReactNode {
  const { startFen, playerColor, startEvalCp, onFinished } = props;

  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  // Bumped on restart / take back so a reply still in flight can't land on the new line.
  const generationRef = useRef(0);

  const [level, setLevel] = useState<PlayLevel>(() => loadPlayLevel());
  const [line, setLine] = useState<LinePosition[]>([{ fen: startFen }]);
  const [cursor, setCursor] = useState(0);
  const [thinking, setThinking] = useState(false);
  const [engineFailed, setEngineFailed] = useState(false);
  // The piece the engine would move, ringed on request, held with the position it was asked about so
  // it drops when the position changes. Free.
  const [hint, setHint] = useState<{ fen: string; square: Key } | null>(null);
  const [hinting, setHinting] = useState(false);

  // Win chance of the live position from the player's side, cached per FEN.
  const winCacheRef = useRef<Map<string, number>>(new Map());
  const storedStartWin = useMemo(() => {
    if (startEvalCp == null) return null;
    const white = whiteWinPct(startEvalCp);
    return playerColor === 'white' ? white : 100 - white;
  }, [startEvalCp, playerColor]);
  // Seeded from the stored evaluation, then replaced by the browser engine's own read, so the bar and
  // "started at N%" come from the same engine at the same depth.
  const [startWin, setStartWin] = useState<number | null>(storedStartWin);
  const [playerWin, setPlayerWin] = useState<number | null>(storedStartWin);

  const liveIndex = line.length - 1;
  const liveFen = line[liveIndex].fen;
  const atLive = cursor >= liveIndex;
  const result = useMemo(() => resultOf(liveFen, playerColor), [liveFen, playerColor]);
  const turn: Color = liveFen.split(' ')[1] === 'b' ? 'black' : 'white';
  const playerToMove = turn === playerColor;

  // Report a finished game once, not on every re-render that recomputes the result.
  const reportedRef = useRef<string | null>(null);
  useEffect(() => {
    if (result == null) {
      reportedRef.current = null;
      return;
    }
    if (reportedRef.current === liveFen) return;
    reportedRef.current = liveFen;
    onFinished?.(result.outcome, level.key);
    // onFinished is a fresh closure each render; keying on the position keeps this once-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, liveFen]);

  // Create the board once; moves go through a ref so the handler sees current state.
  const moveRef = useRef<(orig: Key, dest: Key) => void>(() => {});
  useEffect(() => {
    if (!boardRef.current) return;
    const cg = Chessground(boardRef.current, {
      fen: startFen,
      orientation: playerColor,
      coordinates: true,
      animation: { enabled: true, duration: 200 },
      movable: {
        free: false,
        showDests: true,
        events: { after: (o, d) => moveRef.current(o as Key, d as Key) },
      },
      // Shapes are drawn by the app (the hint ring), never by the player.
      drawable: { enabled: false, visible: true, brushes: HINT_BRUSHES },
    });
    apiRef.current = cg;
    // Same modal-rect problem as CorrectionBoard: redraw once the dialog settles.
    const settle = () => apiRef.current?.redrawAll();
    const frame = requestAnimationFrame(settle);
    const dialog = boardRef.current.closest('.modal');
    dialog?.addEventListener('transitionend', settle);
    return () => {
      cancelAnimationFrame(frame);
      dialog?.removeEventListener('transitionend', settle);
      generationRef.current += 1;
      cg.destroy();
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push a move onto the live line. Returns the position it reached, or null if it was illegal.
  const applyMove = (fen: string, orig: string, dest: string): string | null => {
    try {
      const c = new Chess(fen);
      if (c.move({ from: orig, to: dest, promotion: 'q' }) == null) return null;
      return c.fen();
    } catch {
      return null;
    }
  };

  const handleMove = (orig: Key, dest: Key) => {
    // Only the live position is playable. Chessground already moved the piece, so put it back.
    if (!atLive || !playerToMove || thinking || result != null) {
      apiRef.current?.set({ fen: line[Math.min(cursor, liveIndex)].fen });
      return;
    }
    const after = applyMove(liveFen, orig, dest);
    if (after == null) return;
    setLine((l) => [...l, { fen: after, lastMove: [orig, dest] }]);
    setCursor((c) => c + 1);
  };
  moveRef.current = handleMove;

  // The engine answers whenever it's its turn at the live position. Keyed on the position so take back
  // and restart re-trigger it correctly.
  useEffect(() => {
    if (result != null || playerToMove || !atLive) return;
    if (!stockfishEngine.isSupported()) {
      setEngineFailed(true);
      return;
    }
    const generation = generationRef.current;
    let cancelled = false;
    setThinking(true);
    const started = Date.now();
    stockfishEngine
      .playMove(liveFen, { skill: level.skill, depth: level.depth })
      .then(async (uci) => {
        // Hold a beat so the reply reads as an answer.
        const elapsed = Date.now() - started;
        if (elapsed < MIN_REPLY_MS) {
          await new Promise((r) => setTimeout(r, MIN_REPLY_MS - elapsed));
        }
        if (cancelled || generationRef.current !== generation) return;
        if (uci == null || uci.length < 4) return;
        const after = applyMove(liveFen, uci.slice(0, 2), uci.slice(2, 4));
        if (after == null) return;
        setLine((l) => [...l, { fen: after, lastMove: [uci.slice(0, 2), uci.slice(2, 4)] }]);
        setCursor((c) => c + 1);
      })
      .catch((err) => {
        if (cancelled || generationRef.current !== generation) return;
        clientLog.warn('Play-out engine move failed', {
          fen: liveFen,
          skill: level.skill,
          depth: level.depth,
          message: err instanceof Error ? err.message : String(err),
        });
        setEngineFailed(true);
      })
      .finally(() => {
        // Not gated on `cancelled`: navigating back cancels the reply, and `thinking` must still clear.
        if (generationRef.current === generation) setThinking(false);
      });
    return () => {
      cancelled = true;
    };
    // level is read when the engine is asked, so a change applies from the next reply.
  }, [liveFen, playerToMove, atLive, result, level.skill, level.depth]);

  // The eval bar runs at full strength regardless of the level being played.
  useEffect(() => {
    const fen = line[Math.min(cursor, liveIndex)].fen;
    const cached = winCacheRef.current.get(fen);
    if (cached != null) {
      setPlayerWin(cached);
      return;
    }
    if (!stockfishEngine.isSupported()) return;
    let cancelled = false;
    stockfishEngine
      .evaluate(fen, { depth: 10 })
      .then((e) => {
        if (cancelled) return;
        // Both fields: mate 0 carries the winner in the score's sign.
        const white = whiteWinPct(e.scoreCp, e.mateIn);
        const mine = playerColor === 'white' ? white : 100 - white;
        winCacheRef.current.set(fen, mine);
        setPlayerWin(mine);
        // Same engine and depth as the bar, so the two can't disagree about the start.
        if (fen === startFen) setStartWin(mine);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cursor, line, liveIndex, playerColor, startFen]);

  // Sync the board to the cursor.
  useEffect(() => {
    const cg = apiRef.current;
    if (!cg) return;
    const pos = line[Math.min(cursor, liveIndex)];
    const posTurn: Color = pos.fen.split(' ')[1] === 'b' ? 'black' : 'white';
    const canMove = atLive && playerToMove && !thinking && result == null;
    cg.set({
      fen: pos.fen,
      orientation: playerColor,
      turnColor: posTurn,
      lastMove: pos.lastMove as [Key, Key] | undefined,
      check: new Chess(pos.fen).inCheck() ? posTurn : undefined,
      movable: {
        free: false,
        color: canMove ? playerColor : undefined,
        dests: canMove ? toDests(new Chess(pos.fen)) : new Map(),
      },
    });
    // The ring only belongs to the position it was asked about.
    cg.setShapes(
      hint != null && hint.fen === pos.fen ? [{ orig: hint.square, brush: 'hintPiece' }] : [],
    );
  }, [cursor, line, liveIndex, atLive, playerToMove, thinking, result, playerColor, hint]);

  // Take back until it's the player's move again; undoing only your own move would just replay the
  // engine's reply.
  const takeBack = () => {
    generationRef.current += 1;
    setThinking(false);
    // Drop at least one ply, then keep going until the player is on move (or at the start).
    let next = line.slice(0, -1);
    while (next.length > 1) {
      const t: Color = next[next.length - 1].fen.split(' ')[1] === 'b' ? 'black' : 'white';
      if (t === playerColor) break;
      next = next.slice(0, -1);
    }
    setLine(next);
    setCursor(next.length - 1);
  };

  // Ring the piece the engine would move, not the move: enough to get unstuck. Full strength
  // regardless of level.
  const takeHint = () => {
    if (hinting || !atLive || !playerToMove || thinking || result != null) return;
    if (!stockfishEngine.isSupported()) {
      setEngineFailed(true);
      return;
    }
    const fen = liveFen;
    setHinting(true);
    stockfishEngine
      .evaluate(fen, { depth: HINT_DEPTH })
      .then((e) => {
        if (e.bestMove == null || e.bestMove.length < 4) return;
        setHint({ fen, square: e.bestMove.slice(0, 2) as Key });
      })
      .catch((err) => {
        clientLog.warn('Play-out hint failed', {
          fen,
          message: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => setHinting(false));
  };

  const restart = () => {
    generationRef.current += 1;
    setThinking(false);
    setEngineFailed(false);
    setLine([{ fen: startFen }]);
    setCursor(0);
  };

  const pickLevel = (l: PlayLevel) => {
    setLevel(l);
    savePlayLevel(l.key);
  };

  // Plies played out from the starting position.
  const pliesPlayed = liveIndex;

  const status = (() => {
    if (result != null) {
      const head =
        result.outcome === 'won'
          ? 'You won it.'
          : result.outcome === 'lost'
            ? 'You lost it.'
            : 'Drawn.';
      return { tone: result.outcome, text: `${head} ${result.reason}` };
    }
    if (engineFailed) {
      return { tone: 'idle' as const, text: 'The engine isn’t available in this browser.' };
    }
    if (!atLive)
      return { tone: 'idle' as const, text: 'Looking back — jump to the end to keep playing.' };
    if (thinking) return { tone: 'idle' as const, text: `${level.label} is thinking…` };
    return { tone: 'idle' as const, text: 'Your move.' };
  })();

  return (
    <div className="cm-playout">
      {playerWin != null ? (
        <div
          className={`cm-eval-bar cm-playout-eval-bar${playerColor === 'black' ? ' viewer-black' : ''}`}
          role="progressbar"
          aria-valuenow={playerWin}
          aria-valuemin={0}
          aria-valuemax={100}
          title={`Your win chance: ${playerWin}%`}
        >
          <div className="cm-eval-left" style={{ width: `${playerWin}%` }}>
            <span className="cm-eval-num">{playerWin}%</span>
          </div>
          <span className="cm-eval-num cm-eval-right-num">{100 - playerWin}%</span>
        </div>
      ) : null}

      <div className="cm-playout-board" ref={boardRef} />

      <div className={`cm-playout-status is-${status.tone}`}>
        {thinking ? <span className="cm-playout-spinner" aria-hidden="true" /> : null}
        {status.text}
        {/* Where you started, so the bar has something to be read against. */}
        {startWin != null && pliesPlayed > 0 ? (
          <span className="cm-playout-started">started at {startWin}%</span>
        ) : null}
      </div>

      <div className="cm-playout-controls">
        <Dropdown>
          <Dropdown.Toggle variant="link" className="cm-playout-level" id="playout-level">
            <FontAwesomeIcon icon={faRobot} className="me-1" />
            {level.label}
          </Dropdown.Toggle>
          <Dropdown.Menu className="cm-playout-menu">
            <Dropdown.Header>How strong the engine plays</Dropdown.Header>
            {PLAY_LEVELS.map((l) => (
              <label
                key={l.key}
                className={`cm-playout-level-row${l.key === level.key ? ' is-on' : ''}`}
              >
                <input
                  type="radio"
                  name="playout-level"
                  checked={l.key === level.key}
                  onChange={() => pickLevel(l)}
                />
                <span className="cm-playout-level-label">{l.label}</span>
                <span className="cm-playout-level-about">{l.about}</span>
              </label>
            ))}
            <div className="cm-playout-menu-note">
              Ratings are rough. A new level applies from the engine’s next move.
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <div className="cm-playout-nav">
          <button
            type="button"
            className="cm-correct-ctrl"
            onClick={() => setCursor(0)}
            disabled={cursor <= 0}
            title="Back to the starting position"
            aria-label="Starting position"
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
          <button
            type="button"
            className="cm-correct-ctrl"
            onClick={() => setCursor((c) => Math.max(0, c - 1))}
            disabled={cursor <= 0}
            title="Previous move"
            aria-label="Previous move"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <button
            type="button"
            className="cm-correct-ctrl"
            onClick={() => setCursor((c) => Math.min(liveIndex, c + 1))}
            disabled={cursor >= liveIndex}
            title="Next move"
            aria-label="Next move"
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button
            type="button"
            className="cm-correct-ctrl"
            onClick={() => setCursor(liveIndex)}
            disabled={cursor >= liveIndex}
            title="Back to the live position"
            aria-label="Live position"
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        </div>

        <button
          type="button"
          className="cm-playout-action"
          onClick={takeHint}
          disabled={hinting || !atLive || !playerToMove || thinking || result != null}
          title="Ring the piece the engine would move from here"
        >
          <FontAwesomeIcon icon={faLightbulb} className="me-1" />
          {hinting ? 'Thinking…' : 'Hint'}
        </button>
        <button
          type="button"
          className="cm-playout-action"
          onClick={takeBack}
          disabled={pliesPlayed === 0}
          title="Undo your last move and the reply to it"
        >
          <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
          Take back
        </button>
        <button
          type="button"
          className="cm-playout-action"
          onClick={restart}
          disabled={pliesPlayed === 0}
          title="Back to the position from your game"
        >
          <FontAwesomeIcon icon={faRotateRight} className="me-1" />
          Restart
        </button>
      </div>
    </div>
  );
}
